import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { QuoteOrderLogModelType } from '@teinor/erp/types/company/order/quote/quoteOrder/quoteOrderLog'
import OrderStatusBar from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar'
import { displayDate } from '../../../../../../../../utils/globals/date'
import { Fragment } from 'react'
import { BuyDeliveryNoteLogModelType } from '@teinor/erp/types/company/order/buy/buyDeliveryNote/buyDeliveryNoteLog'
import { getOrderStatusBarType } from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar/functions'
import { processedTypesInversed } from '@teinor/erp/types/company/order/general'
import { BuyDeliveryNoteStatusInversed } from '@teinor/erp/types/company/order/buy/buyDeliveryNote'
import OrderStatusBarDropDown from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBarDropDown'
import { faClock, faPenCircle } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons'

interface BuyDeliveryNoteStatusBarProps extends DeliveryNoteCreateStatusBarProps {}

const BuyDeliveryNoteStatusBar: React.FC<BuyDeliveryNoteStatusBarProps> = ({ orderStatus, OrderLogs, BuyOrderId }) => {
    const { statuses } = useCreateStatusBar({ orderStatus, OrderLogs, BuyOrderId })
    return (
        <OrderStatusBar statuses={statuses} currentOrderStatus={orderStatus} getOrderStatusBarType={getOrderStatusBarType}/>
    )
}

export interface DeliveryNoteCreateStatusBarProps {
    BuyOrderId?: number
    orderStatus: number
    OrderLogs: QuoteOrderLogModelType[]
}

const useCreateStatusBar = ({ orderStatus, OrderLogs, BuyOrderId }: DeliveryNoteCreateStatusBarProps) => {
    const { t } = useTwinTranslation()
    let statuses: TwinDictionary[] = []
    const dictOrderLogs: TwinDictionary = {}
    for (const log of OrderLogs) {
        if (!dictOrderLogs[log.logType]) {
            dictOrderLogs[log.logType] = [log]
        } else {
            dictOrderLogs[log.logType] = [...dictOrderLogs[log.logType], log]
        }
    }
    if (BuyOrderId) {
        statuses.push(
            {
                id: -1,
                name: t('buyOrder', 'Pedido'),
                logType: 0,
                color: 'bg-green-43',
                icon: faCheckCircle,
                className: 'text-green-43',
                renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage BuyOrderId={BuyOrderId} logs={dictOrderLogs[0] || []}  /> : null
            },
            {
                id: 0,
                name: t('draft', 'Borrador'),
                logType: 1,
                color: 'bg-gray-33',
                icon: faPenCircle,
                className: 'text-gray-33',
                extraClassName: 'border border-gray-33',
                renderMessage: dictOrderLogs[1]?.length ? <RenderLogMessage BuyOrderId={BuyOrderId} logs={dictOrderLogs[1] || []} /> : null
            }
        )
    } else {
        statuses.push({
            id: 0,
            name: t('draft', 'Borrador'),
            logType: 0,
            color: 'bg-gray-33',
            icon: faPenCircle,
            className: 'text-gray-33',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage BuyOrderId={BuyOrderId} logs={dictOrderLogs[0] || []} /> : null
        })
    }
    statuses.push(
        {
            id: 1,
            name: t('pendingInvoice', 'Pendiente de facturar'),
            logType: 2,
            color: 'bg-orange-DE',
            icon: faClock,
            className: 'text-orange-DE',
            extraClassName: 'border border-orange-DE',
            renderMessage: dictOrderLogs[2]?.length ? <RenderLogMessage BuyOrderId={BuyOrderId} logs={dictOrderLogs[2] || []} /> : null
        }
    )
    if (orderStatus === processedTypesInversed['canceled']) {
        statuses.push({
            id: 3,
            name: t('deniedP', 'Cancelado'),
            logType: 4,
            color: 'bg-red-BA',
            icon: faXmarkCircle,
            className: 'text-red-BA',
            renderMessage: dictOrderLogs[4]?.length ? <RenderLogMessage BuyOrderId={BuyOrderId} logs={dictOrderLogs[4] || []} /> : null
        })
    } else {
        statuses.push({
            id: 2,
            name: t('invoiced', 'Facturado'),
            logType: 3,
            color: 'bg-green-43',
            icon: faCheckCircle,
            className: 'text-green-43',
            renderMessage: dictOrderLogs[3]?.length ? <RenderLogMessage BuyOrderId={BuyOrderId} logs={dictOrderLogs[3] || []} /> : null
        })
    }
    return { statuses }
}

interface RenderLogMessageProps {
    logs: BuyDeliveryNoteLogModelType[]
    BuyOrderId?: number
}

const RenderLogMessage: React.FC<RenderLogMessageProps> = ({ logs, BuyOrderId }) => {
    const { tVars } = useTwinTranslation()
    const renderMessage: JSX.Element[] = []
    for (const log of logs) {
        const extraDataParsed = JSON.parse(log.extraData || '{}')
        const options = { employeeTrigger: log.EmployeeTrigger?.fullname_short || '', date: displayDate(new Date(log.createdAt)), fullAssignedNumber: extraDataParsed.full_assigned_number || '','interpolation': { 'escapeValue': false }, }
        const dictOrderLogsMessages: TwinDictionary = {
            0: BuyOrderId ? tVars('createdDeliveryNoteFromBuyLogMessage', '{{employeeTrigger}} creó el albarán el {{date}} a partir del pedido {{fullAssignedNumber}}', options) : tVars('createdDeliveryNoteLogMessage', '{{employeeTrigger}} creó el pedido el {{date}}', options),
            1:'',
            2: tVars('waitingResponseDeliveryNoteLogMessage', '{{employeeTrigger}} marcó el albarán como pendiente de facturar el {{date}}', options),
            3: tVars('acceptedDeliveryNoteLogMessage', '{{employeeTrigger}} marcó el albarán como aceptado el {{date}}', options),
            4: tVars('deniedDeliveryNoteLogMessage', '{{employeeTrigger}} marcó el albarán como denegado el {{date}}', options),
        }
        if (dictOrderLogsMessages[log.logType]) {
            renderMessage.push(<div className='my-5' key={log.id} > {dictOrderLogsMessages[log.logType]} </div>)
        }
    }
    if (!renderMessage.length) {
        return null
    }
    return (<Fragment>{renderMessage}</Fragment>)
}

export const BuyDeliveryNoteStatusBarNew: React.FC<BuyDeliveryNoteStatusBarProps> = ({ orderStatus, OrderLogs, BuyOrderId }) => {
    const { statuses } = useCreateStatusBar({ orderStatus, OrderLogs, BuyOrderId })
    return (
        <OrderStatusBarDropDown statuses={statuses} currentOrderStatus={orderStatus} getOrderStatusBarType={getOrderStatusBarType} isAccepted={orderStatus === BuyDeliveryNoteStatusInversed['accepted']} />
    )
}

export default BuyDeliveryNoteStatusBar